import React, { useState } from "react";
import { businessSignup } from "../ApiCalls/authentication";
import { useNavigate } from "react-router-dom";
import mantisLogo from "../Assets/img/mantisBlackLogo.png";
import sandyLogo from "../Assets/img/sandyBlackLogo.png";
import { brand } from "../config";
import { connect } from "react-redux";
import {
  userLogin,
  setUser,
  setToken,
  setUserId,
  setIsBusiness,
  setMantisBusiness,
} from "../Redux/modules/auth";
import { uploadImage } from "../ApiCalls/uploadFiles";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  User,
  Building2,
  FileText,
  UploadCloud,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  X,
  CheckCircle2,
} from "lucide-react";

function mapDispatchToProps(dispatch) {
  return {
    userLogin: () => dispatch(userLogin()),
    setUser: (user) => dispatch(setUser(user)),
    setUserId: (userId) => dispatch(setUserId(userId)),
    setToken: (token) => dispatch(setToken(token)),
    setIsBusiness: (isBusiness) => dispatch(setIsBusiness(isBusiness)),
    setMantisBusiness: (mantisBusiness) =>
      dispatch(setMantisBusiness(mantisBusiness)),
  };
}

const select = (state) => {
  return {
    user: state.auth.user,
  };
};

function BusinessSignup(props) {
  const [currentStep, setCurrentStep] = useState(1);
  const [email, setEmail] = useState("");
  const [emailConfirm, setEmailConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [contactFirstName, setContactFirstName] = useState("");
  const [contactLastName, setContactLastName] = useState("");
  const [identificationType, setIdentificationType] = useState("NIT");
  const [identificationNumber, setIdentificationNumber] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [contactPhone] = useState("");
  const [signingUp, setSigningUp] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();

  // Validations per step
  const isEmailValid = Boolean(email && email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/));
  const isEmailMatch = Boolean(email && emailConfirm && email === emailConfirm);
  const isStep1Valid = Boolean(
    contactFirstName.trim() &&
    contactLastName.trim() &&
    isEmailValid &&
    isEmailMatch
  );

  const isStep2Valid = Boolean(
    businessName.trim() &&
    identificationNumber.trim()
  );

  const isPasswordLengthValid = password.length >= 8;
  const isPasswordMatch = Boolean(password && passwordConfirm && password === passwordConfirm);
  const isStep3Valid = Boolean(
    isPasswordLengthValid &&
    isPasswordMatch &&
    acceptedTerms
  );

  function handleNextStep(e) {
    e.preventDefault();
    setErrorMessage("");
    if (currentStep === 1 && isStep1Valid) {
      setCurrentStep(2);
    } else if (currentStep === 2 && isStep2Valid) {
      setCurrentStep(3);
    }
  }

  function handlePrevStep() {
    setErrorMessage("");
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }

  async function submitSignup(event) {
    event.preventDefault();
    if (!isStep3Valid || signingUp) return;

    setErrorMessage("");
    setSigningUp(true);

    try {
      let logoUrl = "";
      if (logoFile) {
        if (logoFile.size > 20000000) {
          setErrorMessage("El tamaño máximo del logo es 20MB.");
          setSigningUp(false);
          return;
        }
        const formData = new FormData();
        formData.append("image", logoFile);

        const thumbnailJson = await uploadImage(formData);
        if (thumbnailJson && thumbnailJson.imageUrl) {
          logoUrl = thumbnailJson.imageUrl;
        } else {
          setErrorMessage("Error al subir el logo. Intenta de nuevo.");
          setSigningUp(false);
          return;
        }
      }

      const signupRes = await businessSignup(
        businessName,
        contactPhone,
        contactFirstName,
        contactLastName,
        "",
        [],
        [],
        "",
        logoUrl,
        identificationType,
        identificationNumber,
        email,
        password
      );

      if (signupRes && signupRes.success) {
        await updateUserSession(
          signupRes.userId,
          signupRes.user,
          signupRes.token,
          signupRes.mantisBusiness
        );
      } else {
        setErrorMessage(
          signupRes?.message || "Error al registrar la cuenta. Verifica los datos."
        );
      }
    } catch (err) {
      console.error("Signup error:", err);
      setErrorMessage("No se pudo conectar con el servidor. Intenta de nuevo.");
    } finally {
      setSigningUp(false);
    }
  }

  function updateUserSession(userId, user, token, mantisBusiness) {
    props.setUser({ user });
    props.setToken({ token });
    props.setUserId({ userId });
    props.setMantisBusiness({ mantisBusiness });
    props.userLogin();
    props.setIsBusiness({ isBusiness: true });
    navigate("/business/backoffice");
  }

  return (
    <>
      {/* Keyframe Animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes cardEntry {
            0% {
              opacity: 0;
              transform: translateY(24px) scale(0.98);
            }
            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          @keyframes stepSlideIn {
            0% {
              opacity: 0;
              transform: translateX(14px);
            }
            100% {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes floatOrb1 {
            0%, 100% {
              transform: translate(0px, 0px) scale(1);
            }
            50% {
              transform: translate(25px, -20px) scale(1.08);
            }
          }
          @keyframes floatOrb2 {
            0%, 100% {
              transform: translate(0px, 0px) scale(1);
            }
            50% {
              transform: translate(-20px, 25px) scale(0.95);
            }
          }
          @keyframes subtleFloat {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-6px);
            }
          }
          .animate-card-entry {
            animation: cardEntry 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
          .animate-step-slide {
            animation: stepSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
          .animate-float-1 {
            animation: floatOrb1 10s ease-in-out infinite;
          }
          .animate-float-2 {
            animation: floatOrb2 12s ease-in-out infinite;
          }
          .animate-subtle-float {
            animation: subtleFloat 5s ease-in-out infinite;
          }
          .btn-glow-shimmer {
            background-size: 200% 100%;
            transition: all 0.3s ease;
          }
          .btn-glow-shimmer:hover {
            background-position: right center;
          }
        `
      }} />

      {/* Master Container - Exactly Identical to Login */}
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-4 sm:p-6 lg:p-8 relative overflow-hidden">
        {/* Subtle Background Ambient Lights */}
        <div className="absolute top-[-10%] left-[-10%] w-[45vw] h-[45vw] rounded-full bg-gradient-to-br from-orange-200/20 to-transparent blur-3xl pointer-events-none animate-float-1"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[45vw] h-[45vw] rounded-full bg-gradient-to-tl from-amber-200/20 to-transparent blur-3xl pointer-events-none animate-float-2"></div>

        {/* Master Card - Exactly Same Size, Proportions & Height as Login (max-w-5xl, min-h-[580px], grid-cols-2) */}
        <div className="relative w-full max-w-5xl bg-white rounded-[32px] shadow-[0_25px_70px_-15px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.04)] border border-slate-200/80 overflow-hidden grid lg:grid-cols-2 min-h-[580px] z-10 animate-card-entry">
          
          {/* Left Column - Identical Layout, Proportions & Padding */}
          <div className="bg-gradient-to-br from-orange-100/70 via-amber-50/60 to-orange-50/80 p-10 sm:p-14 lg:p-16 flex flex-col justify-center items-center text-center relative overflow-hidden border-b lg:border-b-0 lg:border-r-2 border-orange-200/70">
            {/* Animated Ambient Light Blobs */}
            <div className="absolute -top-16 -left-16 w-80 h-80 bg-gradient-to-br from-orange-300/30 to-amber-200/20 rounded-full blur-3xl pointer-events-none animate-float-1"></div>
            <div className="absolute -bottom-16 -right-16 w-80 h-80 bg-gradient-to-tl from-orange-400/20 to-amber-300/25 rounded-full blur-3xl pointer-events-none animate-float-2"></div>

            <div className="relative z-10 max-w-md mx-auto space-y-6 flex flex-col items-center">
              {/* Large Mantis Logo - Completely Static */}
              <div className="flex justify-center">
                <img
                  src={brand === "MANTIS" ? mantisLogo : sandyLogo}
                  alt="Logo Mantis"
                  className="h-20 sm:h-24 md:h-28 w-auto object-contain drop-shadow-sm"
                />
              </div>

              {/* Centered Title */}
              <div className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-800 tracking-tight">
                  Bienvenido a Mantis
                </h2>
              </div>

              {/* Minimalist Step Breadcrumbs */}
              <div className="flex items-center gap-2 pt-2">
                <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-all ${currentStep > 1 ? "bg-emerald-500 text-white shadow-sm" : currentStep === 1 ? "bg-orange-500 text-white shadow-sm shadow-orange-500/30" : "bg-white/80 text-slate-400 border border-slate-200"}`}>
                  {currentStep > 1 ? "✓" : "1"}
                </div>
                <div className={`w-8 h-0.5 rounded-full transition-all ${currentStep > 1 ? "bg-emerald-500" : "bg-orange-200/80"}`}></div>
                <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-all ${currentStep > 2 ? "bg-emerald-500 text-white shadow-sm" : currentStep === 2 ? "bg-orange-500 text-white shadow-sm shadow-orange-500/30" : "bg-white/80 text-slate-400 border border-slate-200"}`}>
                  {currentStep > 2 ? "✓" : "2"}
                </div>
                <div className={`w-8 h-0.5 rounded-full transition-all ${currentStep === 3 ? "bg-orange-500" : "bg-orange-200/80"}`}></div>
                <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-all ${currentStep === 3 ? "bg-orange-500 text-white shadow-sm shadow-orange-500/30" : "bg-white/80 text-slate-400 border border-slate-200"}`}>
                  3
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Identical Padding & Alignment to Login Form */}
          <div className="p-10 sm:p-14 lg:p-16 flex flex-col justify-center bg-white">
            <div className="max-w-md w-full mx-auto space-y-5">
              
              {/* Stepper Progress Header */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-orange-600">
                    Paso {currentStep} de 3
                  </span>
                  <span className="text-xs font-semibold text-slate-400">
                    {currentStep === 1 && "Datos de Contacto"}
                    {currentStep === 2 && "Datos de Empresa"}
                    {currentStep === 3 && "Seguridad de Acceso"}
                  </span>
                </div>
                
                {/* Visual Progress Bar */}
                <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 transition-all duration-500 ease-out rounded-full"
                    style={{
                      width: currentStep === 1 ? "33.33%" : currentStep === 2 ? "66.66%" : "100%",
                    }}
                  ></div>
                </div>
              </div>

              {/* Error Banner */}
              {errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-2.5 flex items-start space-x-2 text-red-700 text-xs shadow-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-red-500" />
                  <div className="flex-1">
                    <p>{errorMessage}</p>
                  </div>
                </div>
              )}

              {/* STEP 1: Datos de Contacto */}
              {currentStep === 1 && (
                <form onSubmit={handleNextStep} className="space-y-3.5 animate-step-slide">
                  {/* Left-Aligned Header */}
                  <div className="text-left space-y-1">
                    <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight text-left">
                      Datos del Responsable
                    </h1>
                    <p className="text-xs sm:text-sm text-slate-500 text-left">
                      Ingresa tu nombre y correo corporativo para administrar la cuenta.
                    </p>
                  </div>

                  <div className="space-y-3 pt-1">
                    {/* Row 1: First Name & Last Name (2 cols) */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1 text-left">
                        <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block">
                          Nombre(s) *
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                            <User className="h-4 w-4" />
                          </div>
                          <input
                            type="text"
                            value={contactFirstName}
                            onChange={(e) => setContactFirstName(e.target.value)}
                            placeholder="Juan Carlos"
                            className="w-full pl-9 pr-3.5 py-2.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 focus:border-orange-500 rounded-xl text-slate-900 placeholder-slate-400 text-xs sm:text-sm font-medium focus:ring-4 focus:ring-orange-500/10 transition-all outline-none shadow-sm"
                            required
                            autoFocus
                          />
                        </div>
                      </div>

                      <div className="space-y-1 text-left">
                        <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block">
                          Apellido(s) *
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                            <User className="h-4 w-4" />
                          </div>
                          <input
                            type="text"
                            value={contactLastName}
                            onChange={(e) => setContactLastName(e.target.value)}
                            placeholder="Pérez García"
                            className="w-full pl-9 pr-3.5 py-2.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 focus:border-orange-500 rounded-xl text-slate-900 placeholder-slate-400 text-xs sm:text-sm font-medium focus:ring-4 focus:ring-orange-500/10 transition-all outline-none shadow-sm"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Row 2: Email (Full Width) */}
                    <div className="space-y-1 text-left">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block">
                        Correo Electrónico Corporativo *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                          <Mail className="h-4 w-4" />
                        </div>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="contacto@empresa.com"
                          className="w-full pl-10 pr-4 py-2.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 focus:border-orange-500 rounded-xl text-slate-900 placeholder-slate-400 text-xs sm:text-sm font-medium focus:ring-4 focus:ring-orange-500/10 transition-all outline-none shadow-sm"
                          required
                        />
                      </div>
                    </div>

                    {/* Row 3: Confirm Email (Full Width) */}
                    <div className="space-y-1 text-left">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block">
                        Confirmar Correo Electrónico *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                          <Mail className="h-4 w-4" />
                        </div>
                        <input
                          type="email"
                          value={emailConfirm}
                          onChange={(e) => setEmailConfirm(e.target.value)}
                          placeholder="contacto@empresa.com"
                          className={`w-full pl-10 pr-4 py-2.5 bg-slate-50 hover:bg-white focus:bg-white border rounded-xl text-slate-900 placeholder-slate-400 text-xs sm:text-sm font-medium focus:ring-4 focus:ring-orange-500/10 transition-all outline-none shadow-sm ${
                            emailConfirm && email !== emailConfirm
                              ? "border-red-400 focus:border-red-500"
                              : "border-slate-200 focus:border-orange-500"
                          }`}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {emailConfirm && email !== emailConfirm && (
                    <p className="text-xs text-red-500 font-medium text-left">Los correos electrónicos no coinciden</p>
                  )}

                  {/* Step 1 Button */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={!isStep1Valid}
                      className="w-full py-3.5 px-6 bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-bold text-sm shadow-md shadow-orange-500/25 hover:shadow-lg hover:shadow-orange-500/35 transition-all duration-200 flex items-center justify-center gap-2 transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed btn-glow-shimmer"
                    >
                      <span>Continuar a Datos de Empresa</span>
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </form>
              )}

              {/* STEP 2: Datos de Empresa */}
              {currentStep === 2 && (
                <form onSubmit={handleNextStep} className="space-y-3.5 animate-step-slide">
                  <div className="text-left space-y-1">
                    <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight text-left">
                      Información de tu Empresa
                    </h1>
                    <p className="text-xs sm:text-sm text-slate-500 text-left">
                      Datos legales y logotipo de la organización.
                    </p>
                  </div>

                  <div className="space-y-3 pt-1">
                    {/* Business Name */}
                    <div className="space-y-1 text-left">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block">
                        Razón Social / Nombre de la Empresa *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                          <Building2 className="h-4 w-4" />
                        </div>
                        <input
                          type="text"
                          value={businessName}
                          onChange={(e) => setBusinessName(e.target.value)}
                          placeholder="Ej. Inversiones Mantis S.A.S."
                          className="w-full pl-10 pr-4 py-2.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 focus:border-orange-500 rounded-xl text-slate-900 placeholder-slate-400 text-xs sm:text-sm font-medium focus:ring-4 focus:ring-orange-500/10 transition-all outline-none shadow-sm"
                          required
                          autoFocus
                        />
                      </div>
                    </div>

                    {/* Identification (NIT / Number) */}
                    <div className="space-y-1 text-left">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block">
                        Identificación Tributaria *
                      </label>
                      <div className="flex gap-2.5">
                        <div className="w-24 flex-shrink-0">
                          <select
                            value={identificationType}
                            onChange={(e) => setIdentificationType(e.target.value)}
                            className="w-full py-2.5 px-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-700 text-xs font-bold outline-none cursor-pointer text-center"
                          >
                            <option value="NIT">NIT</option>
                            <option value="CC">CC</option>
                            <option value="CE">CE</option>
                          </select>
                        </div>
                        <div className="relative flex-1">
                          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                            <FileText className="h-4 w-4" />
                          </div>
                          <input
                            type="text"
                            value={identificationNumber}
                            onChange={(e) => setIdentificationNumber(e.target.value)}
                            placeholder="900123456-1"
                            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 focus:border-orange-500 rounded-xl text-slate-900 placeholder-slate-400 text-xs sm:text-sm font-medium focus:ring-4 focus:ring-orange-500/10 transition-all outline-none shadow-sm"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Logo Upload */}
                    <div className="space-y-1 text-left">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block">
                        Logotipo de la Empresa (Opcional)
                      </label>
                      <div className="relative">
                        <input
                          type="file"
                          id="companyLogoInput"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => setLogoFile(e.target.files[0] || null)}
                        />
                        <div
                          onClick={() => document.getElementById("companyLogoInput").click()}
                          className="w-full py-2.5 px-4 bg-slate-50 hover:bg-slate-100/80 border border-dashed border-slate-300 hover:border-orange-400 rounded-xl flex items-center justify-between cursor-pointer transition-all group"
                        >
                          <div className="flex items-center gap-2.5 text-xs text-slate-600">
                            <UploadCloud className="h-5 w-5 text-slate-400 group-hover:text-orange-500 transition-colors" />
                            <span className="truncate max-w-[240px]">
                              {logoFile ? `Logo: ${logoFile.name}` : "Cargar imagen de logo (PNG, JPG)"}
                            </span>
                          </div>
                          {logoFile ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setLogoFile(null);
                              }}
                              className="p-1 hover:bg-slate-200 rounded-full text-slate-400 hover:text-slate-600"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          ) : (
                            <span className="text-xs font-semibold text-orange-600 group-hover:underline">
                              Examinar
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Step 2 Actions */}
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handlePrevStep}
                      className="w-full py-3.5 px-4 text-slate-700 bg-slate-100 hover:bg-slate-200/80 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-1.5 border border-slate-200/60"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      <span>Atrás</span>
                    </button>
                    <button
                      type="submit"
                      disabled={!isStep2Valid}
                      className="w-full py-3.5 px-4 bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-bold text-sm shadow-md shadow-orange-500/25 hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed btn-glow-shimmer"
                    >
                      <span>Continuar</span>
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </form>
              )}

              {/* STEP 3: Seguridad y Finalización */}
              {currentStep === 3 && (
                <form onSubmit={submitSignup} className="space-y-3.5 animate-step-slide">
                  <div className="text-left space-y-1">
                    <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight text-left">
                      Seguridad y Términos
                    </h1>
                    <p className="text-xs sm:text-sm text-slate-500 text-left">
                      Crea tu clave de acceso segura y completa el registro.
                    </p>
                  </div>

                  <div className="space-y-3 pt-1">
                    {/* Password */}
                    <div className="space-y-1 text-left">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block">
                        Contraseña *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                          <Lock className="h-4 w-4" />
                        </div>
                        <input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Mínimo 8 caracteres"
                          className="w-full pl-10 pr-10 py-2.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 focus:border-orange-500 rounded-xl text-slate-900 placeholder-slate-400 text-xs sm:text-sm font-medium focus:ring-4 focus:ring-orange-500/10 transition-all outline-none shadow-sm"
                          required
                          minLength={8}
                          disabled={signingUp}
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                          disabled={signingUp}
                          aria-label="Alternar visibilidad"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Confirm Password */}
                    <div className="space-y-1 text-left">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block">
                        Confirmar Contraseña *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                          <Lock className="h-4 w-4" />
                        </div>
                        <input
                          type={showPasswordConfirm ? "text" : "password"}
                          value={passwordConfirm}
                          onChange={(e) => setPasswordConfirm(e.target.value)}
                          placeholder="Confirma tu contraseña"
                          className={`w-full pl-10 pr-10 py-2.5 bg-slate-50 hover:bg-white focus:bg-white border rounded-xl text-slate-900 placeholder-slate-400 text-xs sm:text-sm font-medium focus:ring-4 focus:ring-orange-500/10 transition-all outline-none shadow-sm ${
                            passwordConfirm && password !== passwordConfirm
                              ? "border-red-400 focus:border-red-500"
                              : "border-slate-200 focus:border-orange-500"
                          }`}
                          required
                          minLength={8}
                          disabled={signingUp}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                          className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                          disabled={signingUp}
                          aria-label="Alternar visibilidad"
                        >
                          {showPasswordConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Password Checklist */}
                    <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-2.5 flex items-center justify-around text-xs">
                      <div className={`flex items-center gap-1.5 ${isPasswordLengthValid ? "text-emerald-600 font-medium" : "text-slate-400"}`}>
                        <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[9px] ${isPasswordLengthValid ? "bg-emerald-100 text-emerald-600" : "bg-slate-200 text-slate-400"}`}>
                          ✓
                        </div>
                        <span>Mínimo 8 caracteres</span>
                      </div>
                      <div className={`flex items-center gap-1.5 ${isPasswordMatch ? "text-emerald-600 font-medium" : "text-slate-400"}`}>
                        <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[9px] ${isPasswordMatch ? "bg-emerald-100 text-emerald-600" : "bg-slate-200 text-slate-400"}`}>
                          ✓
                        </div>
                        <span>Contraseñas coinciden</span>
                      </div>
                    </div>

                    {/* Terms Checkbox */}
                    <div className="pt-0.5 text-left">
                      <label className="flex items-start gap-2.5 cursor-pointer text-xs text-slate-600 leading-relaxed">
                        <input
                          type="checkbox"
                          checked={acceptedTerms}
                          onChange={(e) => setAcceptedTerms(e.target.checked)}
                          required
                          className="mt-0.5 rounded border-slate-300 text-orange-600 focus:ring-orange-500 h-4 w-4 cursor-pointer"
                          disabled={signingUp}
                        />
                        <span>
                          Acepto las{" "}
                          <a
                            href="/data-treatment-policies"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-orange-600 hover:text-orange-700 font-semibold hover:underline"
                          >
                            Políticas de Tratamiento de Datos
                          </a>{" "}
                          y los{" "}
                          <a
                            href="/terms-and-conditions"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-orange-600 hover:text-orange-700 font-semibold hover:underline"
                          >
                            Términos y Condiciones
                          </a>{" "}
                          de Mantis App S.A.S.
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Step 3 Actions */}
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handlePrevStep}
                      disabled={signingUp}
                      className="w-full py-3.5 px-4 text-slate-700 bg-slate-100 hover:bg-slate-200/80 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-1.5 border border-slate-200/60"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      <span>Atrás</span>
                    </button>
                    <button
                      type="submit"
                      disabled={!isStep3Valid || signingUp}
                      className="w-full py-3.5 px-4 bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-bold text-sm shadow-md shadow-orange-500/25 hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed btn-glow-shimmer"
                    >
                      {signingUp ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Creando...</span>
                        </>
                      ) : (
                        <>
                          <span>Crear Cuenta</span>
                          <CheckCircle2 className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}

              {/* Login Link */}
              <div className="text-center pt-3 border-t border-slate-100 mt-2">
                <p className="text-xs sm:text-sm text-slate-600 inline-flex items-center justify-center flex-wrap gap-1.5 mx-auto text-center">
                  <span>¿Ya tienes una cuenta corporativa?</span>
                  <button
                    type="button"
                    onClick={() => navigate("/")}
                    className="text-orange-600 hover:text-orange-700 font-bold hover:underline transition-colors cursor-pointer"
                    disabled={signingUp}
                  >
                    Inicia sesión aquí
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default connect(select, mapDispatchToProps)(BusinessSignup);
